import React, { Component } from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import * as THREE from 'three'
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'

import Loading from '../utilities/loading'
import { fetchScanAvatar, resetScanAvatar } from '../../actions/scanAvatar'

import './scans.css'

const MAX_POLAR_ANGLE = (4 / 8) * Math.PI // the max position toward feet
const MIN_POLAR_ANGLE = (4 / 8) * Math.PI // the max position toward head

class ScanAvatar extends Component {
  constructor(props) {
    super(props)

    this._mounted = false
    this.containerRef = React.createRef()

    this.state = {
      loading: true,
      error: false,
    }

    // Three.js objects
    this.scene = null
    this.camera = null
    this.renderer = null
    this.avatar = null
    this.animationId = null
    this.controls = null
  }

  componentDidMount() {
    this._mounted = true
    this.initRendering()
    if (this.props.scanPackageId) {
      this.props.fetchScanAvatar(this.props.scanPackageId)
    }
  }

  componentDidUpdate(prevProps) {
    // Check if scanPackageId changed
    if (this.props.scanPackageId !== prevProps.scanPackageId) {
      if (this.props.scanPackageId) {
        // Reset local error state when starting a new fetch
        this.setState({ error: false })
        this.props.fetchScanAvatar(this.props.scanPackageId)
      } else {
        this.props.resetScanAvatar()
      }
    }

    // Check if error state changed - clear local loading if there's a Redux error
    const prevError = prevProps.scanAvatar.get('error')
    const currentError = this.props.scanAvatar.get('error')

    if (prevError !== currentError && currentError) {
      this.setState({ loading: false, error: true })
    }

    // Check if scan avatar data changed
    const prevData = prevProps.scanAvatar.get('scanAvatarData')
    const currentData = this.props.scanAvatar.get('scanAvatarData')

    if (prevData !== currentData && currentData) {
      this.loadAvatarFromData(currentData.toJS())
    }
  }

  componentWillUnmount() {
    this._mounted = false

    // Clean up Three.js resources
    if (this.animationId) {
      window.cancelAnimationFrame(this.animationId)
    }

    if (this.controls) {
      this.controls.dispose()
    }

    if (this.renderer) {
      this.renderer.dispose()
    }

    if (this.containerRef.current && this.renderer) {
      this.containerRef.current.removeChild(this.renderer.domElement)
    }

    // Reset Redux state
    this.props.resetScanAvatar()
  }

  initRendering() {
    if (!this.containerRef.current) return

    // Scene
    this.scene = new THREE.Scene()
    this.scene.background = new THREE.Color(0xffffff)

    // Camera
    this.camera = new THREE.PerspectiveCamera(
      10,
      this.containerRef.current.clientWidth /
        this.containerRef.current.clientHeight,
      1,
      100
    )
    this.camera.position.set(0, 0, 6)
    this.camera.lookAt(0, 0, 0)

    // Renderer
    this.renderer = new THREE.WebGLRenderer({ antialias: true })
    this.renderer.setSize(
      this.containerRef.current.clientWidth,
      this.containerRef.current.clientHeight
    )
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    this.renderer.toneMapping = THREE.NeutralToneMapping
    this.renderer.toneMappingExposure = 2.5
    this.renderer.outputColorSpace = THREE.SRGBColorSpace
    this.containerRef.current.appendChild(this.renderer.domElement)

    // Lighting — soft light tent (even at all rotation angles)
    const hemiLight = new THREE.HemisphereLight(0xffffff, 0xd0d0d0, 4.0)
    this.scene.add(hemiLight)

    // Four soft directional lights evenly spaced around the model
    const lightIntensity = 1.0
    const lightPositions = [
      [5, 5, 5], // front-right
      [-5, 5, 5], // front-left
      [5, 5, -5], // back-right
      [-5, 5, -5], // back-left
    ]
    for (const pos of lightPositions) {
      const light = new THREE.DirectionalLight(0xfff8f0, lightIntensity)
      light.position.set(...pos)
      this.scene.add(light)
    }

    // Orbit Controls
    this.controls = new OrbitControls(this.camera, this.renderer.domElement)
    this.controls.maxPolarAngle = MAX_POLAR_ANGLE
    this.controls.minPolarAngle = MIN_POLAR_ANGLE
    this.controls.enableDamping = true
    this.controls.dampingFactor = 0.05
    this.controls.enableZoom = false
    this.controls.enableRotate = true
    this.controls.enablePan = false

    // Start render loop
    this.animate()

    // Handle window resize
    window.addEventListener('resize', this.handleResize.bind(this))
  }

  handleResize() {
    if (!this.containerRef.current || !this.camera || !this.renderer) return

    const width = this.containerRef.current.clientWidth
    const height = this.containerRef.current.clientHeight

    this.camera.aspect = width / height
    this.camera.updateProjectionMatrix()
    this.renderer.setSize(width, height)
  }

  loadAvatarFromData(scanAvatarData) {
    if (!scanAvatarData || !scanAvatarData.objAsset) {
      console.warn('No scan avatar data available')
      this.setState({ loading: false, error: true })
      return
    }

    const { objAsset } = scanAvatarData
    const loader = new OBJLoader()
    const textureLoader = new THREE.TextureLoader()

    // Clear existing avatar
    if (this.avatar) {
      this.scene.remove(this.avatar)
      this.avatar = null
    }

    this.setState({ loading: true, error: false })

    // Load texture if available
    const loadModel = (texture) => {
      if (!objAsset.modelUrl) {
        console.warn('No model URL available in scan data')
        if (this._mounted) {
          this.setState({ loading: false, error: true })
        }
        return
      }

      loader.load(
        objAsset.modelUrl,
        (object) => {
          if (!this._mounted) return

          // Apply PBR material to all meshes
          if (texture) {
            texture.colorSpace = THREE.SRGBColorSpace
          }
          object.traverse((child) => {
            if (child.isMesh) {
              child.material = texture
                ? new THREE.MeshStandardMaterial({
                    map: texture,
                    roughness: 0.7,
                    metalness: 0.0,
                  })
                : new THREE.MeshStandardMaterial({
                    color: 0xcccccc,
                    roughness: 0.9,
                    metalness: 0.0,
                  })
            }
          })

          // Scale and position the model
          object.scale.setScalar(0.5)
          object.position.set(0, -0.5, 0)

          // Rotate the avatar 90 degrees around Z-axis
          object.rotateZ(Math.PI / 2)

          this.avatar = object
          this.scene.add(object)

          this.setState({ loading: false, error: false })
        },
        // Progress callback
        (progress) => {
          console.log(
            'Loading progress:',
            (progress.loaded / progress.total) * 100 + '%'
          )
        },
        // Error callback
        (error) => {
          console.error('Error loading avatar:', error)
          if (this._mounted) {
            this.setState({ loading: false, error: true })
          }
        }
      )
    }

    // Load texture if available, otherwise load model without texture
    if (objAsset.textureUrl) {
      textureLoader.load(
        objAsset.textureUrl,
        (texture) => {
          loadModel(texture)
        },
        // Texture progress callback
        undefined,
        // Texture error callback
        (error) => {
          console.warn(
            'Error loading texture, proceeding without texture:',
            error
          )
          loadModel(null) // Load model without texture
        }
      )
    } else {
      loadModel(null) // No texture available
    }
  }

  animate() {
    if (!this._mounted) return

    this.animationId = window.requestAnimationFrame(this.animate.bind(this))

    // Update controls
    if (this.controls) {
      this.controls.update()
    }

    // Rotate the avatar slowly
    if (this.avatar) {
      this.avatar.rotation.y += 0.005
    }

    this.renderer.render(this.scene, this.camera)
  }

  renderLoading() {
    const isLoading = this.state.loading || this.props.scanAvatar.get('loading')
    const hasError = this.state.error || this.props.scanAvatar.get('error')

    // Don't show loading if there's an error
    return isLoading && !hasError ? <Loading /> : null
  }

  render() {
    if (!this.props.scanPackageId) {
      return <div />
    }

    const scanAvatarError = this.props.scanAvatar.get('error')
    const hasError = this.state.error || scanAvatarError

    return (
      <div>
        {this.renderLoading()}
        {hasError && (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: '600px' }}
          >
            <div>
              <div className="text-center mb-2">
                <span
                  className="fas fa-cube"
                  style={{ color: '#484848', fontSize: '35px' }}
                />
              </div>
              <div>
                {scanAvatarError === 404
                  ? 'No 3D color model available for this scan'
                  : 'No 3D color model available'}
              </div>
            </div>
          </div>
        )}
        <div
          ref={this.containerRef}
          style={{
            width: '100%',
            height: '600px',
            display: hasError ? 'none' : 'block',
          }}
        />
      </div>
    )
  }
}

ScanAvatar.propTypes = {
  // Needed from parent components
  scanPackageId: PropTypes.string.isRequired,
  // Redux props
  scanAvatar: ImmutablePropTypes.map.isRequired,
  fetchScanAvatar: PropTypes.func.isRequired,
  resetScanAvatar: PropTypes.func.isRequired,
}

const mapStateToProps = (state) => ({
  scanAvatar: state.scanAvatar,
})

const mapDispatchToProps = (dispatch) => ({
  fetchScanAvatar: bindActionCreators(fetchScanAvatar, dispatch),
  resetScanAvatar: bindActionCreators(resetScanAvatar, dispatch),
})

export default connect(mapStateToProps, mapDispatchToProps)(ScanAvatar)
